import type { Activity, ChatMessage, DemoData, Health, HomeTheme, Privacy, PulseComment, PulseItem, ShareRecord, Task, TimelineUpdate } from '../domain/types';
import type { CalView } from './selectors';
import { seriesLabel } from '../domain/series';

export type DiscoverTab = 'Activities' | 'Help' | 'People' | 'UNITS';
export type SheetState =
  | { kind: 'update'; activityId: string }
  | { kind: 'help'; activityId: string }
  | { kind: 'quickShare'; kitId: string }
  | { kind: 'profile'; section?: EditSection };

/** Edit profile sections, in the order of the editor's icon row. */
export const EDIT_SECTIONS = ['intro', 'units', 'photos', 'appearance', 'referral', 'privacy'] as const;
export type EditSection = (typeof EDIT_SECTIONS)[number];

export interface UiState {
  sheet: SheetState | null;
  toast: { id: number; text: string } | null;
  pulseFilter: string;
  discover: { tab: DiscoverTab; series: string; q: string; status: string };
  calendar: { view: CalView; day: string | null };
}

export interface AppState { data: DemoData; ui: UiState }

export const initialUi: UiState = {
  sheet: null,
  toast: null,
  pulseFilter: 'All',
  discover: { tab: 'Activities', series: 'All', q: '', status: 'Any status' },
  calendar: { view: 'Mine', day: null },
};

export type DataAction =
  | { type: 'task/toggle'; taskId: string }
  | { type: 'task/add'; task: Task }
  | { type: 'activity/health'; activityId: string; health: Health }
  | { type: 'update/add'; update: TimelineUpdate; pulse?: PulseItem }
  | { type: 'activity/propose'; activity: Activity; update: TimelineUpdate; pulse?: PulseItem }
  | { type: 'help/post'; activityId: string; roles: string[]; pulse: PulseItem }
  | { type: 'help/withdraw'; activityId: string; role: string }
  | { type: 'follow/activity'; activityId: string }
  | { type: 'follow/person'; personId: string }
  | { type: 'offer/toggle'; activityId: string }
  | { type: 'offer/accept'; offerId: string; message: ChatMessage }
  | { type: 'chat/send'; message: ChatMessage }
  | { type: 'pulse/comment'; pulseId: string; comment: PulseComment }
  | { type: 'share/record'; record: ShareRecord }
  | { type: 'profile/bio'; personId: string; bio: string }
  | { type: 'profile/privacy'; key: keyof Privacy; value: boolean }
  | { type: 'profile/unitReferral'; personId: string; unitId: string; referralUrl: string }
  | { type: 'profile/affiliations'; personId: string; affiliations: string[]; primary: string }
  | { type: 'profile/theme'; theme: HomeTheme }
  | { type: 'demo/reset'; data: DemoData };

export type UiAction =
  | { type: 'ui/sheet'; sheet: SheetState | null }
  | { type: 'ui/toast'; id: number; text: string }
  | { type: 'ui/clearToast'; id: number }
  | { type: 'ui/pulseFilter'; filter: string }
  | { type: 'ui/discover'; patch: Partial<UiState['discover']> }
  | { type: 'ui/calendar'; patch: Partial<UiState['calendar']> };

export type Action = DataAction | UiAction;

function toggle(r: Record<string, true>, k: string): Record<string, true> {
  const n = { ...r };
  if (n[k]) delete n[k];
  else n[k] = true;
  return n;
}

export function dataReducer(d: DemoData, a: DataAction): DemoData {
  switch (a.type) {
    case 'task/add':
      return { ...d, tasks: [...d.tasks, a.task] };
    case 'task/toggle':
      return { ...d, tasks: d.tasks.map((t) => (t.id === a.taskId ? { ...t, done: !t.done } : t)) };
    case 'activity/health': {
      const act = d.activities[a.activityId];
      return act ? { ...d, activities: { ...d.activities, [act.id]: { ...act, health: a.health } } } : d;
    }
    case 'update/add':
      return { ...d, updates: [a.update, ...d.updates], pulse: a.pulse ? [a.pulse, ...d.pulse] : d.pulse };
    case 'activity/propose':
      return {
        ...d,
        activities: { ...d.activities, [a.activity.id]: a.activity },
        updates: [a.update, ...d.updates],
        pulse: a.pulse ? [a.pulse, ...d.pulse] : d.pulse,
      };
    case 'help/post': {
      const helpPosted = { ...d.helpPosted };
      a.roles.forEach((r) => (helpPosted[`${a.activityId}|${r}`] = true));
      return { ...d, helpPosted, pulse: [a.pulse, ...d.pulse] };
    }
    case 'help/withdraw': {
      const key = `${a.activityId}|${a.role}`;
      const helpPosted = { ...d.helpPosted };
      delete helpPosted[key];
      return { ...d, helpPosted, pulse: d.pulse.filter((p) => p.helpKey !== key) };
    }
    case 'follow/activity':
      return { ...d, followedActivities: toggle(d.followedActivities, a.activityId) };
    case 'follow/person':
      return { ...d, followedPeople: toggle(d.followedPeople, a.personId) };
    case 'offer/toggle':
      return { ...d, offeredHelp: toggle(d.offeredHelp, a.activityId) };
    case 'offer/accept': {
      const offer = d.offers.find((o) => o.id === a.offerId);
      if (!offer || offer.status === 'accepted') return d;
      const act = d.activities[offer.activityId];
      const team = act.team.some((m) => m.ambassadorId === offer.ambassadorId)
        ? act.team
        : [...act.team, { ambassadorId: offer.ambassadorId, role: `${offer.role} (offered help)` }];
      return {
        ...d,
        offers: d.offers.map((o) => (o.id === offer.id ? { ...o, status: 'accepted' } : o)),
        activities: { ...d.activities, [act.id]: { ...act, team } },
        chats: [...d.chats, a.message],
      };
    }
    case 'chat/send':
      return { ...d, chats: [...d.chats, a.message] };
    case 'pulse/comment':
      return { ...d, pulse: d.pulse.map((p) => (p.id === a.pulseId ? { ...p, comments: [...p.comments, a.comment] } : p)) };
    case 'share/record':
      return { ...d, shareRecords: [a.record, ...d.shareRecords] };
    case 'profile/bio': {
      const p = d.ambassadors[a.personId];
      return p ? { ...d, ambassadors: { ...d.ambassadors, [p.id]: { ...p, bio: a.bio } } } : d;
    }
    case 'profile/unitReferral': {
      const p = d.ambassadors[a.personId];
      return p ? { ...d, ambassadors: { ...d.ambassadors, [p.id]: { ...p, unitId: a.unitId || undefined, referralUrl: a.referralUrl || undefined } } } : d;
    }
    case 'profile/affiliations': {
      const p = d.ambassadors[a.personId];
      if (!p) return d;
      const affiliations = a.primary ? [a.primary, ...a.affiliations.filter((s) => s !== a.primary)] : [];
      const units = affiliations.map((id) => p.units.find((u) => u.series === seriesLabel(d.series, id)) ?? { name: p.city || 'Worldwide', series: seriesLabel(d.series, id) });
      return { ...d, ambassadors: { ...d.ambassadors, [p.id]: { ...p, series: a.primary, affiliations, units } } };
    }
    case 'profile/privacy':
      return { ...d, privacy: { ...d.privacy, [a.key]: a.value } };
    case 'profile/theme':
      return { ...d, appearance: { ...d.appearance, theme: a.theme } };
    case 'demo/reset':
      return a.data;
  }
}

export function uiReducer(u: UiState, a: UiAction): UiState {
  switch (a.type) {
    case 'ui/sheet':
      return { ...u, sheet: a.sheet };
    case 'ui/toast':
      return { ...u, toast: { id: a.id, text: a.text } };
    case 'ui/clearToast':
      return u.toast?.id === a.id ? { ...u, toast: null } : u;
    case 'ui/pulseFilter':
      return { ...u, pulseFilter: a.filter };
    case 'ui/discover':
      return { ...u, discover: { ...u.discover, ...a.patch } };
    case 'ui/calendar':
      return { ...u, calendar: { ...u.calendar, ...a.patch } };
  }
}

export function reducer(s: AppState, a: Action): AppState {
  if (a.type.startsWith('ui/')) return { ...s, ui: uiReducer(s.ui, a as UiAction) };
  return { ...s, data: dataReducer(s.data, a as DataAction) };
}
